import * as React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { resolve } from 'inversify-react';

import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { ControllerProps } from 'src/router';
import { DialogService } from 'src/core/services/ui/dialog.service';
import { ConfigOptionFormView } from './config-option-form.view';
import { ConfigOptionDataService } from './config-option-form.data';
import { UserListDataService } from 'src/app/general/users/list/user-list.data';
import { ConfigOptionsService } from 'src/core/services/config-options.service';
import { CommunicationService } from 'src/core/services/communication.service';
import { HttpService } from 'src/core/services/http.service';

interface Props extends ControllerProps<{}> {
}

interface State {
	mode: ControlMode;
	refresh: boolean;
	logs: { date: Date, message: string, level: number }[];
}

@observer
export class ConfigOptionFormController extends React.Component<Props, State> {
	@resolve
	dialogService: DialogService;

	@resolve
	toast: ToastNotifications;

	@observable
	dataService: ConfigOptionDataService;

	@observable
	userDataService: UserListDataService;

	@resolve
	configService: ConfigOptionsService;
	
	@resolve
	private communicationService: CommunicationService;

	@resolve
	httpService: HttpService;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			mode: 'display',
			refresh: false,
			logs: []
		}
	}

	showLog = (date: Date, message: string, level: number) => {
		this.setState({ logs: [...this.state.logs, { date, message, level }]})
	}

	entityChangeMessage = (data) => {
		this.setState({ logs: [...this.state.logs, {date: new Date(), level: 1, message: data.TypeName + ' ' + data.Operation}]})
	}

	componentDidMount() {
		this.loadData();
		
		this.communicationService.EventEmitter.on('logMessage', this.showLog);
		this.communicationService.EventEmitter.on('entityChange', this.entityChangeMessage);
	}

	public componentWillUnmount() {

		this.communicationService.EventEmitter.removeListener('logMessage', this.showLog);
		this.communicationService.EventEmitter.removeListener('entityChange', this.entityChangeMessage);
	}

	private loadData(): void {
		this.userDataService = UserListDataService.make();
		this.dataService = ConfigOptionDataService.make();
		this.props.setBusy(true);
		this.dataService.addSorting('key', 'asc', true);
		this.dataService.pageSize = 100;
		this.dataService.load(query => query)
			.then(() => this.props.setBusy(false))
			.catch(error => {
				this.props.setBusy(false);
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			});
	}

	handleModeChange = (mode: ControlMode): void => {
		this.setState({ mode });
	}

	handleImageChange = (ev: React.ChangeEvent<HTMLInputElement>, field: string, size: number) => {
		const file: File = ev.target.files[0];
		if (file) {
			const contentReader = new FileReader();
			if (file.size > size) {
				this.toast.showToast('Image upload error!', `Image too large, has ${file.size} and must be less than ${size / 1024} kilobytes`, 'error');
				return;
			}

			contentReader.onload = (e: Event) => {
				const result: ArrayBuffer = (e.target as any).result;
				const image = new Uint8Array(result)
				const logoItem = this.dataService.items.find(x => x.key == field);
				if (logoItem) {
					if (image != null && image.length > 0) {
						let str = '';
						for (let i = 0; i < image.length; i++) {
							str += String.fromCharCode(image[i]);
						}
						logoItem.value = btoa(str);
						if (field == 'Logo') {
							this.props.setLogo(logoItem.value);
						}
					}
				}
				this.setState({
					refresh: !this.state.refresh
				});
			};
			contentReader.readAsArrayBuffer(file);
		}
	}

	save = (): void => {
		this.props.setBusy(true);
		this.dataService.save()
			.then(() => {
				this.props.setBusy(false);
				this.handleModeChange('display');
				this.toast.showDataSaved();
			})
			.catch((error) => {
				this.props.setBusy(false);
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			});

	}

	cancel = (): Promise<boolean> => {
		return new Promise<boolean>((resolve, reject) => {
			if (this.dataService.isDirty) {
				this.dialogService.showYesNo('Warning', 'Are you sure want to close this form? Your changes will be lost!')
					.then(dialogResult => {
						resolve(true);
					})
					.catch(reject);
			} else {
				this.props.history.push('/');
				resolve(true);
			}
		});
	};

	download_logs = async () => {
		await this.configService.downloadLogs();
	}

	reboot = async () => {
		try {
			await this.configService.reboot();

			this.toast.showToast("The server is rebooting","", "warning");
		}
		catch (e) {
			console.log(e);
			this.toast.showToast("The server is rebooting", ObjectUtils.parseError(e), "error",);
		}
	}

	render() {
		if (this.dataService == null || this.dataService.items.length == 0) {
			return null;
		}

		return (
			<ConfigOptionFormView
				mode={this.state.mode}
				onModeChanged={this.handleModeChange}
				cancel={this.cancel}
				save={this.save}
				model={this.dataService}
				setBusy={this.props.setBusy}
				reboot={this.reboot}
				downloadLogs={this.download_logs}
				logs={this.state.logs}
				onImageChange={this.handleImageChange}
				add={this.addKey}
				resetCacheConnection={this.resetCacheConnection}
			/>
		);
	}

	private addKey = (key: string[]) => {
		this.dataService.addKey(key);
	}

	private resetCacheConnection = () => {
		this.httpService.post("api/v1/cache/reset", {})
			.then(() => this.toast.showToast("Connection reset success!", null, "success"))
			.catch(error => this.toast.showToast("Connection reset failed!", null, "error"))
	}
}




// WEBPACK FOOTER //
// ./src/app/general/config-option/form/config-option-form.controller.tsx